import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import {
  Consultantlogin,
  verifyConsultantOtp,
  forgotPasswordConsultant,
} from "../Services/authService";
import NormalAlert from "../../Alerts/NormalAlert";
import LoginBg from "../../../Assets/Loginbg.svg";
import { Mail, Lock, Eye, EyeOff, RefreshCw } from "lucide-react";

const ConsultantLogin = () => {
  const navigate = useNavigate();
  const captchaRef = useRef(null);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [alert, setAlert] = useState(null);
  const [IsPasswordVisible, setIsPasswordVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isOtpStep, setIsOtpStep] = useState(false);
  const [otpInput, setOtpInput] = useState("");

  // Captcha states
  const [captchaText, setCaptchaText] = useState("");
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [captchaInput, setCaptchaInput] = useState("");

  // Forget Password States
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  useEffect(() => {
    const token = Cookies.get("ConsultantToken");

    if (token) {
      navigate("/Consultant/Dashboard", { replace: true });
    }
  }, [navigate]);

  //  Function to generate captcha locally
  const generateCaptcha = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    const captcha = Array.from({ length: 6 }, () =>
      chars.charAt(Math.floor(Math.random() * chars.length)),
    ).join("");

    setCaptchaText(captcha);
    setCaptchaAnswer(captcha);
    setCaptchaInput("");
  };

  //captcha with random noise and rotation
  useEffect(() => {
    const canvas = captchaRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    ctx.fillStyle = "#EAF4FF";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    if (!captchaText) return;

    // Draw noisy lines
    for (let i = 0; i < 5; i++) {
      ctx.strokeStyle = `rgba(0, 70, 135, ${Math.random() * 0.5 + 0.3})`;
      ctx.beginPath();
      ctx.moveTo(Math.random() * 150, Math.random() * 45);
      ctx.lineTo(Math.random() * 150, Math.random() * 45);
      ctx.stroke();
    }

    // Draw captcha text with random rotation
    ctx.font = "bold 24px monospace";
    ctx.textBaseline = "middle";

    const xStart = 15;
    for (let i = 0; i < captchaText.length; i++) {
      const angle = (Math.random() - 0.5) * 0.4;
      ctx.save();
      ctx.translate(xStart + i * 20, 22);
      ctx.rotate(angle);
      ctx.fillStyle = `rgb(${50 + Math.random() * 100}, ${
        60 + Math.random() * 100
      }, ${120 + Math.random() * 100})`;
      ctx.fillText(captchaText[i], 0, 0);
      ctx.restore();
    }

    // Add random dots
    for (let i = 0; i < 30; i++) {
      ctx.fillStyle = `rgba(0, 0, 0, ${Math.random() * 0.3})`;
      ctx.beginPath();
      ctx.arc(Math.random() * 150, Math.random() * 45, 1, 0, 2 * Math.PI);
      ctx.fill();
    }
  }, [captchaText, isForgotPassword]);

  useEffect(() => {
    if (isForgotPassword) {
      setTimeout(() => {
        generateCaptcha();
      }, 100);
    }
  }, [isForgotPassword]);

  useEffect(() => {
    generateCaptcha();
  }, []);

  // Login
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    const validationErrors = {};
    if (!email.trim()) validationErrors.email = "Email is required.";
    if (!password.trim()) {
      validationErrors.password = "Password is required.";
    } else if (!isStrongPassword(password)) {
      setAlert({
        type: "fail",
        message:
          "Password must be at least 8 characters long and include uppercase, lowercase, number, and special character.",
      });
      setLoading(false);
      return;
    }
    if (!captchaInput.trim())
      validationErrors.captchaInput = "Captcha is required.";
    else if (captchaInput.trim() !== captchaAnswer.trim()) {
      validationErrors.captchaInput =
        "Invalid captcha. Please enter the exact characters (case-sensitive).";
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setLoading(false);
      generateCaptcha();
      setCaptchaInput("");
      return;
    }

    try {
      const response = await Consultantlogin(email, password);
      const data = response.data;

      if (data.status === "Success" && data.authUID) {
        Cookies.set("consultantAuthUID", data.authUID, {
          secure: true,
          sameSite: "Strict",
        });
        setIsOtpStep(true);
        setAlert({
          type: "success",
          message: "OTP sent to your email. Please verify it.",
        });
      } else {
        setAlert({ type: "fail", message: data.message || "Login failed." });
        generateCaptcha();
      }
    } catch (error) {
      setAlert({
        type: "fail",
        message: error?.response?.data?.message || "Something went wrong",
      });
    } finally {
      setLoading(false);
    }
  };

  const isStrongPassword = (password) => {
    const strongPasswordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return strongPasswordRegex.test(password);
  };

  // Verify OTP
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const authUID = Cookies.get("consultantAuthUID");

    if (!otpInput.trim()) {
      setAlert({ type: "fail", message: "Please enter OTP." });
      return;
    }

    try {
      const response = await verifyConsultantOtp(otpInput.trim(), authUID);
      const data = response.data;

      if (data.status === "Success" && data.userDetails?.token) {
        const { token } = data.userDetails;
        Cookies.set("ConsultantToken", token, {
          secure: true,
          sameSite: "Strict",
        });
        Cookies.set("username", email.split("@")[0], {
          secure: true,
          sameSite: "Strict",
        });

        setAlert({ type: "success", message: "OTP verified successfully!" });
        setTimeout(() => navigate("/Consultant/Dashboard"), 1500);
      } else {
        setAlert({
          type: "fail",
          message: data.message || "Invalid OTP. Please try again.",
        });
      }
    } catch (error) {
      setAlert({
        type: "fail",
        message:
          error?.response?.data?.message || "Invalid OTP or expired session.",
      });
    }
  };

  const handleForgotPassword = async () => {
    if (!validateForgotPassword()) {
      generateCaptcha();
      return;
    }

    try {
      const response = await forgotPasswordConsultant(email);
      const data = response.data;

      if (data.status === "Success") {
        setAlert({
          type: "success",
          message: data.message,
        });

        setEmail("");
        setCaptchaInput("");
        generateCaptcha();
        setErrors({});
      } else {
        setAlert({
          type: "fail",
          message: data.message || "Something went wrong",
        });
      }
    } catch (error) {
      setAlert({
        type: "fail",
        message:
          error?.response?.data?.message || "Server error. Please try again.",
      });
    } finally {
      setCaptchaInput("");
      generateCaptcha();
    }
  };

  const validateForgotPassword = () => {
    let newErrors = {};

    if (!email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Invalid email format";
    }

    if (!captchaInput.trim()) {
      newErrors.captchaInput = "Captcha is required";
    } else if (captchaInput.trim() !== captchaAnswer.trim()) {
      newErrors.captchaInput = "Captcha does not match!";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const goToLoginScreen = () => {
    setIsOtpStep(false);
    setIsForgotPassword(false);
    setErrors({});
    setTimeout(() => {
      generateCaptcha();
      setCaptchaInput("");
    }, 100);
  };

  return (
    <>
      <div className="min-h-[100vh] flex flex-col md:flex-row animate-fadeIn">
        {/* LEFT SIDE */}
        <div className="relative w-full md:w-1/2 flex flex-col min-h-screen justify-between pt-[18vh] md:pt-[24vh] px-4 sm:px-6 md:px-20 bg-white">
          {/* Top Gradient Header */}
          <div className="absolute top-0 left-0 w-full h-[18vh] md:h-[22vh] bg-gradient-to-br from-[#e3f2fd] via-[#bbdefb] to-[#90caf9] flex items-center justify-center z-0">
            <Link to="/">
              <img
                src={process.env.PUBLIC_URL + "/HRSetu-final-logo.svg"}
                alt="HR Setu recruitment platform logo"
                className="w-28"
              />
            </Link>
          </div>
          <div className="relative z-10">
            <div className="text-center mb-6">
              {isForgotPassword ? (
                <>
                  <h4 className="text-xl font-semibold text-gray-800 mt-6 sm:mt-10">
                    Forgot your Password?
                  </h4>
                  <p className="text-gray-500">
                    Enter your email and reset your password.
                  </p>
                </>
              ) : isOtpStep ? (
                <>
                  <h4 className="text-xl font-semibold text-gray-800 mt-16">
                    Enter OTP to Verify
                  </h4>
                  <p className="text-gray-500">
                    Please enter the OTP sent to your registered email.
                  </p>
                </>
              ) : (
                <>
                  <h4 className="text-xl font-semibold text-gray-800 mt-6 sm:mt-10">
                    Login to your Consultant account
                  </h4>
                  <p className="text-gray-500">
                    Welcome back, please enter your details
                  </p>
                </>
              )}
            </div>

            {/* FORGOT PASSWORD FORM */}
            {isForgotPassword && (
              <form className="space-y-4 px-3 sm:px-4 md:px-10 max-w-md mx-auto md:max-w-none animate-fadeSlideUp">
                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email address
                  </label>
                  <input
                    type="email"
                    className="w-full py-2.5 sm:py-3 px-4 rounded-lg border bg-gray-50 focus:outline-none focus:ring-2 outline-none border-gray-300 focus:ring-blue-400"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  {errors.email && (
                    <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                  )}
                </div>

                {/* Captcha */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Verification Code
                  </label>

                  <div className="flex items-start gap-4">
                    {/* Captcha Canvas */}
                    <canvas
                      ref={captchaRef}
                      width="150"
                      height="45"
                      className="rounded-md border bg-gray-50"
                    />

                    {/* Refresh */}
                    <button
                      type="button"
                      onClick={generateCaptcha}
                      className="text-blue-600 transition-transform duration-500 hover:rotate-180 mt-2"
                    >
                      <RefreshCw size={20} />
                    </button>

                    {/* Input */}
                    <div className="flex-1">
                      <input
                        type="text"
                        placeholder="Enter Captcha"
                        value={captchaInput}
                        onChange={(e) => {
                          setCaptchaInput(e.target.value);
                          setErrors((prev) => ({ ...prev, captchaInput: "" }));
                        }}
                        className="w-full h-[45px] px-4 rounded-md border bg-gray-50 outline-none border-gray-300 focus:ring-2 focus:ring-blue-400"
                      />

                      {errors.captchaInput && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.captchaInput}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Verify Button */}
                <div className="text-center mt-4">
                  <button
                    type="button"
                    className="w-[180px] h-[48px] bg-sky-500 text-white rounded-md font-semibold shadow-sm transition hover:bg-sky-600"
                    onClick={handleForgotPassword}
                  >
                    Verify
                  </button>

                  {/* Back */}
                  <div className="mt-3">
                    <button
                      type="button"
                      className="text-blue-600 text-sm"
                      onClick={goToLoginScreen}
                    >
                      Back to Login
                    </button>
                  </div>
                </div>
              </form>
            )}

            {/* LOGIN FORM */}
            {!isForgotPassword && !isOtpStep && (
              <form
                onSubmit={handleLogin}
                className="space-y-4 px-4 md:px-10 animate-fadeSlideUp"
              >
                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email address
                  </label>
                  <div className="relative">
                    <Mail
                      className="absolute left-3 top-3 text-gray-400"
                      size={18}
                    />
                    <input
                      type="email"
                      className={`w-full pl-10 pr-4 py-2.5 sm:py-3 rounded-lg border bg-blue-50 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:bg-white transition duration-200 outline-none 
                        ${
                          errors.email
                            ? "border-red-400 focus:ring-2 focus:ring-red-300 focus:bg-white"
                            : "border-gray-300 focus:ring-2 focus:ring-sky-400 focus:bg-white"
                        }`}
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                    {errors.email && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.email}
                      </p>
                    )}
                  </div>
                </div>

                {/* Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password
                  </label>
                  <div className="relative">
                    <Lock
                      className="absolute left-3 top-3 text-gray-400"
                      size={18}
                    />
                    <input
                      type={IsPasswordVisible ? "text" : "password"}
                      className={`w-full pl-10 pr-10 py-2.5 sm:py-3 rounded-lg border bg-blue-50 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:bg-white transition duration-200 outline-none
                         ${
                           errors.password
                             ? "border-red-400 focus:ring-2 focus:ring-red-300 focus:bg-white"
                             : "border-gray-300 focus:ring-2 focus:ring-sky-400 focus:bg-white"
                         }`}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-3 text-gray-500"
                      onClick={() => setIsPasswordVisible(!IsPasswordVisible)}
                    >
                      {IsPasswordVisible ? (
                        <EyeOff size={18} />
                      ) : (
                        <Eye size={18} />
                      )}
                    </button>
                    {errors.password && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.password}
                      </p>
                    )}
                  </div>
                </div>

                {/* Captcha */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Verification Code
                  </label>

                  <div className="flex items-center gap-2 flex-nowrap md:flex-row md:gap-3">
                    {/* Captcha */}
                    <canvas
                      ref={captchaRef}
                      width="150"
                      height="45"
                      className="rounded-md border bg-gray-50 w-[90px] h-[36px] md:w-[150px] md:h-[45px]"
                    />

                    {/* Refresh */}
                    <button
                      type="button"
                      onClick={generateCaptcha}
                      className="text-blue-600"
                    >
                      <RefreshCw size={18} />
                    </button>

                    {/* Input */}
                    <input
                      type="text"
                      placeholder="Enter Captcha"
                      value={captchaInput}
                      onChange={(e) => setCaptchaInput(e.target.value)}
                      className={`flex-1 min-w-0 h-[36px] md:h-[45px] px-2 md:px-4 rounded-md border bg-blue-50 focus:outline-none focus:ring-2 focus:ring-sky-400 ${
                        errors.captchaInput
                          ? "border-red-400"
                          : "border-gray-300"
                      }`}
                    />
                  </div>

                  {errors.captchaInput && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.captchaInput}
                    </p>
                  )}
                </div>

                <div className="text-right pr-1 sm:pr-0">
                  <button
                    type="button"
                    className="text-blue-600 text-sm"
                    onClick={() => setIsForgotPassword(true)}
                  >
                    Forgot Password?
                  </button>
                </div>

                <div className="flex justify-center pt-4">
                  <button
                    type="submit"
                    className="w-full sm:w-[190px] h-[48px] bg-sky-500 text-white rounded-md font-semibold flex justify-center items-center shadow-md transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0"
                  >
                    {loading ? (
                      <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></span>
                    ) : (
                      "Login"
                    )}
                  </button>
                </div>
              </form>
            )}

            {/* OTP FORM */}
            {isOtpStep && !isForgotPassword && (
              <div className="animate-fadeSlideUp mt-6 sm:mt-10 w-full">
                <form
                  onSubmit={handleVerifyOtp}
                  className="w-full max-w-md mx-auto px-4 sm:px-6"
                >
                  {/* Label */}
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    Enter OTP
                  </label>

                  {/* Input */}
                  <input
                    type="password"
                    className="w-full h-[45px] sm:h-[48px] px-3 sm:px-4 rounded-md border border-blue-100 bg-blue-50 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:bg-white transition"
                    placeholder="Enter 6-digit OTP"
                    value={otpInput}
                    maxLength={6}
                    onChange={(e) =>
                      setOtpInput(e.target.value.replace(/\D/g, ""))
                    }
                  />

                  {/* Button */}
                  <div className="flex justify-center mt-6 sm:mt-8">
                    <button
                      type="submit"
                      className="w-full sm:w-[200px] h-[45px] sm:h-[48px] bg-sky-500 text-white rounded-md font-semibold shadow-md transition hover:bg-sky-600"
                    >
                      Verify OTP
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>

          <div className="relative mt-3 md:mt-6 sm:absolute sm:bottom-0 left-1/2 -translate-x-1/2 w-[85%] text-center text-xs text-gray-400 border-t border-gray-200 py-2">
            Designed & Developed by
            <span className="text-blue-500"> VRSamadhan Tech Solution</span>
          </div>
        </div>

        <div className="hidden md:block md:w-1/2">
          <img
            src={LoginBg}
            alt="HR Setu login background showing modern workspace"
            className="w-full h-screen object-cover animate-slowZoom"
          />
        </div>
      </div>

      {alert && (
        <NormalAlert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}
    </>
  );
};

export default ConsultantLogin;
